import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BookOpen,
  Play,
  Loader2,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  AlertCircle,
  X,
  SkipForward,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { NativeSelect } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { aiSummaryApi } from '@/services/api/aiSummary'
import type {
  JobStatusResponse,
  ArticleResult,
  PreviewArticle,
} from '@/services/api/aiSummary'

// ============================================================================
// Preview Article Row
// ============================================================================

function ArticleRow({
  article,
  selected,
  onToggle,
}: {
  article: PreviewArticle
  selected: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={cn(
        'group flex items-center gap-4 px-4 py-3 border-b border-border/50 cursor-pointer transition-colors',
        'hover:bg-muted/50',
        selected && 'bg-primary/5 hover:bg-primary/10'
      )}
      onClick={onToggle}
    >
      <div className="flex-shrink-0">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
      </div>
      <span className="flex-shrink-0 w-36 font-mono text-sm text-muted-foreground">
        {article.display_number}
      </span>
      <span className="flex-1 truncate font-medium">
        {article.short_description || '—'}
      </span>
      <span className="flex-shrink-0 w-12 text-xs text-muted-foreground uppercase text-center">
        {article.language || '—'}
      </span>
      {/* Summary Status Badge */}
      <div className="flex-shrink-0 w-24">
        {article.has_summary ? (
          <span className="text-xs px-2 py-0.5 rounded border bg-green-100 text-green-800 border-green-200">
            Has Summary
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200">
            No Summary
          </span>
        )}
      </div>
      <span className="flex-shrink-0 text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded hidden lg:inline">
        {article.knowledge_base}
      </span>
    </div>
  )
}

function ListHeader({
  onSelectAll,
  allSelected,
  someSelected,
}: {
  onSelectAll: () => void
  allSelected: boolean
  someSelected: boolean
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b border-border bg-muted/30 text-sm text-muted-foreground sticky top-0 z-10">
      <div className="flex-shrink-0">
        <Checkbox
          checked={allSelected}
          onCheckedChange={onSelectAll}
          className={cn(
            someSelected && !allSelected && 'data-[state=checked]:bg-muted'
          )}
        />
      </div>
      <span className="flex-shrink-0 w-36">Article ID</span>
      <span className="flex-1">Title</span>
      <span className="flex-shrink-0 w-12 text-center">Lang</span>
      <span className="flex-shrink-0 w-24 text-center">Summary</span>
      <span className="flex-shrink-0 w-48 text-right hidden lg:block">Knowledge Base</span>
    </div>
  )
}

// ============================================================================
// Result Components
// ============================================================================

function getStatusStyle(status: string) {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'failure':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'skipped':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'success':
      return <CheckCircle2 className="h-4 w-4 text-green-600" />
    case 'failure':
      return <XCircle className="h-4 w-4 text-red-600" />
    case 'skipped':
      return <SkipForward className="h-4 w-4 text-yellow-600" />
    default:
      return null
  }
}

function ResultRow({ result }: { result: ArticleResult }) {
  return (
    <div className="flex items-start gap-4 px-4 py-3 border-b border-border/50 hover:bg-muted/50 transition-colors">
      <div className="flex-shrink-0 mt-0.5">
        <StatusIcon status={result.status} />
      </div>
      <span className="flex-shrink-0 w-36 font-mono text-sm text-muted-foreground">
        {result.display_number}
      </span>
      <span className="flex-1 truncate text-sm">
        {result.short_description || '—'}
      </span>
      <span className="flex-shrink-0 w-12 text-xs text-muted-foreground uppercase text-center">
        {result.language || '—'}
      </span>
      <span
        className="flex-shrink-0 w-72 text-sm text-muted-foreground truncate"
        title={result.ai_summary || result.error || ''}
      >
        {result.ai_summary || result.error || '—'}
      </span>
      <div className="flex-shrink-0">
        <span className={cn('text-xs px-2 py-0.5 rounded border', getStatusStyle(result.status))}>
          {result.status}
        </span>
      </div>
    </div>
  )
}

function ResultsHeader() {
  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b border-border bg-muted/30 text-sm text-muted-foreground sticky top-0 z-10">
      <span className="flex-shrink-0 w-4" />
      <span className="flex-shrink-0 w-36">Article ID</span>
      <span className="flex-1">Title</span>
      <span className="flex-shrink-0 w-12 text-center">Lang</span>
      <span className="flex-shrink-0 w-72">AI Summary / Error</span>
      <span className="flex-shrink-0 w-16 text-center">Status</span>
    </div>
  )
}

// ============================================================================
// Active Job Card (with progress bar)
// ============================================================================

function ActiveJobCard({ job }: { job: JobStatusResponse }) {
  const progress = job.progress

  return (
    <Card className="border-primary/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="font-medium">
              Generating AI Summaries
              {job.knowledge_base && (
                <span className="text-muted-foreground font-normal"> — {job.knowledge_base}</span>
              )}
            </span>
          </div>
          <span className="text-sm text-muted-foreground">
            {progress.successful_articles} success
            {progress.failed_articles > 0 && `, ${progress.failed_articles} failed`}
            {progress.skipped_articles > 0 && `, ${progress.skipped_articles} skipped`}
          </span>
        </div>
        <Progress value={progress.percentage} className="mb-2" />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>
            {progress.processed_articles} of {progress.total_articles} articles
          </span>
          <span>{progress.percentage.toFixed(0)}%</span>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Completed Job Card
// ============================================================================

function CompletedJobCard({
  job,
  onDownload,
  onDismiss,
  isDownloading,
}: {
  job: JobStatusResponse
  onDownload: () => void
  onDismiss: () => void
  isDownloading: boolean
}) {
  const progress = job.progress
  const allSuccess = progress.failed_articles === 0 && progress.skipped_articles === 0
  const hasFailures = progress.failed_articles > 0
  const isFailed = job.status === 'failed'

  return (
    <Card
      className={cn(
        'border-2',
        isFailed && 'border-red-500/50 bg-red-50/50',
        !isFailed && allSuccess && 'border-green-500/50 bg-green-50/50',
        !isFailed && hasFailures && 'border-red-500/50 bg-red-50/50',
        !isFailed && !allSuccess && !hasFailures && 'border-yellow-500/50 bg-yellow-50/50'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isFailed ? (
              <XCircle className="h-6 w-6 text-red-600" />
            ) : allSuccess ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : hasFailures ? (
              <XCircle className="h-6 w-6 text-red-600" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <p className="font-medium">
                {isFailed ? 'Job Failed' : 'AI Summary Generation Complete'}
                {job.knowledge_base && (
                  <span className="text-muted-foreground font-normal">
                    {' '}— {job.knowledge_base}
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {isFailed
                  ? (job.error_message || 'An error occurred')
                  : `${progress.successful_articles} success, ${progress.skipped_articles} skipped, ${progress.failed_articles} failed of ${progress.total_articles} articles`
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isFailed && progress.successful_articles > 0 && (
              <Button
                onClick={onDownload}
                disabled={isDownloading}
                size="sm"
                variant="outline"
                className="bg-white"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download Excel
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onDismiss}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// LocalStorage keys
// ============================================================================

const ACTIVE_JOB_KEY = 'ai_summary_active_job_id'
const COMPLETED_JOB_KEY = 'ai_summary_completed_job'

// ============================================================================
// Main Page
// ============================================================================

export function AISummaryPage() {
  const queryClient = useQueryClient()

  // Filters
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [articleIds, setArticleIds] = useState('')
  const [summaryStatus, setSummaryStatus] = useState<string>('without_summary')
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())

  // Job state - persisted in localStorage
  const [activeJobId, setActiveJobId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_JOB_KEY)
    }
    return null
  })
  const [completedJob, setCompletedJob] = useState<JobStatusResponse | null>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COMPLETED_JOB_KEY)
      if (stored) {
        try { return JSON.parse(stored) } catch { return null }
      }
    }
    return null
  })

  // Persist job state
  useEffect(() => {
    if (activeJobId) {
      localStorage.setItem(ACTIVE_JOB_KEY, activeJobId)
    } else {
      localStorage.removeItem(ACTIVE_JOB_KEY)
    }
  }, [activeJobId])

  useEffect(() => {
    if (completedJob) {
      localStorage.setItem(COMPLETED_JOB_KEY, JSON.stringify(completedJob))
    } else {
      localStorage.removeItem(COMPLETED_JOB_KEY)
    }
  }, [completedJob])

  // Fetch knowledge bases
  const { data: kbData } = useQuery({
    queryKey: ['ai-summary', 'knowledge-bases'],
    queryFn: () => aiSummaryApi.getKnowledgeBases(),
  })

  // Build the article_ids query param for the preview
  const trimmedArticleIds = articleIds
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(',')

  // Fetch article preview - always enabled (defaults to showing articles without summary)
  const {
    data: preview,
    isLoading: previewLoading,
    isFetching: previewFetching,
    isError: previewError,
  } = useQuery({
    queryKey: ['ai-summary', 'preview', knowledgeBase, trimmedArticleIds, summaryStatus],
    queryFn: () =>
      aiSummaryApi.preview({
        knowledge_base: knowledgeBase || undefined,
        article_ids: trimmedArticleIds || undefined,
        summary_status: summaryStatus || undefined,
      }),
    staleTime: 30000,
  })

  // Poll active job status (every 1.5s)
  const { data: activeJob } = useQuery({
    queryKey: ['ai-summary', 'job', activeJobId],
    queryFn: () => aiSummaryApi.getJobStatus(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 1500
      if (['completed', 'failed'].includes(data.status)) return false
      return 1500
    },
  })

  // Move job to completed when finished
  if (activeJob && ['completed', 'failed'].includes(activeJob.status) && activeJobId) {
    setCompletedJob(activeJob)
    setActiveJobId(null)
    queryClient.invalidateQueries({ queryKey: ['ai-summary'] })
  }

  // Clear selection when filters change
  const resetSelection = useCallback(() => {
    setSelectedArticles(new Set())
  }, [])

  useEffect(() => {
    resetSelection()
  }, [knowledgeBase, trimmedArticleIds, summaryStatus, resetSelection])

  // Submit job mutation
  const submitJob = useMutation({
    mutationFn: () => {
      const selectedIds =
        selectedArticles.size > 0 ? Array.from(selectedArticles) : undefined

      const parsedArticleIds = trimmedArticleIds
        ? trimmedArticleIds.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined

      // When no specific selection and no KB/article filter, use all visible articles
      const allVisibleIds = !selectedIds && !parsedArticleIds && !knowledgeBase && articles.length > 0
        ? articles.map((a) => a.display_number)
        : undefined

      return aiSummaryApi.generate({
        knowledge_base: selectedIds || allVisibleIds ? undefined : knowledgeBase || undefined,
        article_ids: selectedIds || parsedArticleIds || allVisibleIds || undefined,
      })
    },
    onSuccess: (data) => {
      setActiveJobId(data.job_id)
      setSelectedArticles(new Set())
    },
  })

  // Download mutation
  const downloadMutation = useMutation({
    mutationFn: (jobId: string) => aiSummaryApi.downloadExcel(jobId),
  })

  // Selection helpers
  const articles = preview?.articles || []

  const toggleArticle = (displayNumber: string) => {
    const next = new Set(selectedArticles)
    if (next.has(displayNumber)) {
      next.delete(displayNumber)
    } else {
      next.add(displayNumber)
    }
    setSelectedArticles(next)
  }

  const toggleSelectAll = () => {
    if (selectedArticles.size === articles.length) {
      setSelectedArticles(new Set())
    } else {
      setSelectedArticles(new Set(articles.map((a) => a.display_number)))
    }
  }

  const canGenerate = articles.length > 0 && !submitJob.isPending && !activeJobId

  const handleReset = () => {
    setKnowledgeBase('')
    setArticleIds('')
    setSummaryStatus('without_summary')
    setSelectedArticles(new Set())
    setCompletedJob(null)
  }

  // Is the job actively running?
  const isJobRunning = !!activeJobId && activeJob && !['completed', 'failed'].includes(activeJob.status)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <BookOpen className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">AI Summary Generator</h1>
          <p className="text-muted-foreground">
            Generate and publish AI summaries to ServiceNow
          </p>
        </div>
      </div>

      {/* Active Job Progress */}
      <AnimatePresence>
        {isJobRunning && activeJob && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <ActiveJobCard job={activeJob} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completed Job */}
      <AnimatePresence>
        {completedJob && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CompletedJobCard
              job={completedJob}
              onDownload={() => downloadMutation.mutate(completedJob.job_id)}
              onDismiss={() => setCompletedJob(null)}
              isDownloading={downloadMutation.isPending}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {/* Knowledge Base */}
            <div className="flex-1 min-w-[250px]">
              <label className="text-sm font-medium mb-1 block">Knowledge Base</label>
              <NativeSelect
                value={knowledgeBase}
                onChange={(e) => {
                  setKnowledgeBase(e.target.value)
                  setArticleIds('')
                }}
                options={[
                  { value: '', label: 'All Knowledge Bases' },
                  ...(kbData?.knowledge_bases || []).map((kb) => ({
                    value: kb.title,
                    label: `${kb.title} (${kb.without_summary_count} without summary)`,
                  })),
                ]}
              />
            </div>

            {/* Article IDs */}
            <div className="flex-1 min-w-[250px]">
              <label className="text-sm font-medium mb-1 block">
                Search by KB Number
              </label>
              <Input
                placeholder="e.g. KB0071349, KB0056182"
                value={articleIds}
                onChange={(e) => setArticleIds(e.target.value)}
                className="h-10"
              />
            </div>

            {/* Summary Status Filter */}
            <div className="flex-1 min-w-[180px]">
              <label className="text-sm font-medium mb-1 block">Summary Status</label>
              <NativeSelect
                value={summaryStatus}
                onChange={(e) => setSummaryStatus(e.target.value)}
                options={[
                  { value: 'without_summary', label: 'Without Summary' },
                  { value: 'with_summary', label: 'With Summary' },
                  { value: '', label: 'All Articles' },
                ]}
              />
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={handleReset}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Action Bar */}
      {articles.length > 0 && !isJobRunning && (
        <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">
              {preview?.total_articles || 0} articles found
            </span>
            {previewFetching && !previewLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {selectedArticles.size > 0 && (
              <Badge variant="secondary" className="font-normal">
                {selectedArticles.size} selected
              </Badge>
            )}
          </div>
          <Button
            onClick={() => submitJob.mutate()}
            disabled={!canGenerate}
            size="sm"
          >
            {submitJob.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {selectedArticles.size > 0
              ? `Generate for ${selectedArticles.size} Selected`
              : `Generate for All ${preview?.total_articles || 0}`}
          </Button>
        </div>
      )}

      {/* Submit Error */}
      {submitJob.isError && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-medium text-red-800">Failed to Submit Job</p>
                <p className="text-sm text-red-600">
                  {submitJob.error?.message || 'An unexpected error occurred'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Table (after job completes) */}
      {completedJob &&
        completedJob.results.length > 0 && (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between py-3 px-4 bg-muted/30 border-b">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  {completedJob.progress.total_articles} articles processed
                </span>
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {completedJob.progress.successful_articles} success
                </Badge>
                {completedJob.progress.skipped_articles > 0 && (
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                    {completedJob.progress.skipped_articles} skipped
                  </Badge>
                )}
                {completedJob.progress.failed_articles > 0 && (
                  <Badge variant="secondary" className="bg-red-100 text-red-800">
                    {completedJob.progress.failed_articles} failed
                  </Badge>
                )}
              </div>
            </div>
            <ResultsHeader />
            <div className="max-h-[600px] overflow-y-auto">
              {completedJob.results.map((result, idx) => (
                <ResultRow key={`${result.display_number}-${idx}`} result={result} />
              ))}
            </div>
          </Card>
        )}

      {/* Article Preview List (before job runs) */}
      {!completedJob && !isJobRunning && (
        <Card className="overflow-hidden">
          {previewLoading ? (
            <div className="divide-y divide-border/50">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-10" />
                </div>
              ))}
            </div>
          ) : previewError ? (
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p className="text-lg font-medium">Failed to load articles</p>
              <p className="text-muted-foreground">
                Please check your connection and try again
              </p>
            </CardContent>
          ) : articles.length === 0 ? (
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium">No articles found</p>
              <p className="text-muted-foreground">
                Try adjusting your filters to see more results
              </p>
            </CardContent>
          ) : (
            <>
              <ListHeader
                onSelectAll={toggleSelectAll}
                allSelected={
                  selectedArticles.size === articles.length && articles.length > 0
                }
                someSelected={
                  selectedArticles.size > 0 &&
                  selectedArticles.size < articles.length
                }
              />
              <div className="max-h-[600px] overflow-y-auto">
                {articles.map((article) => (
                  <ArticleRow
                    key={article.display_number}
                    article={article}
                    selected={selectedArticles.has(article.display_number)}
                    onToggle={() => toggleArticle(article.display_number)}
                  />
                ))}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  )
}
